import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, inrMoney, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, X, AlertTriangle, Loader2, BadgeIndianRupee, Filter, CalendarCheck, Download } from "lucide-react";

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-900",
  preparing: "bg-blue-100 text-blue-900",
  ready: "bg-emerald-100 text-emerald-900",
  completed: "bg-cream-300 text-ink-700",
  delivered: "bg-slate-200 text-slate-900",
  cancelled: "bg-red-100 text-red-900",
  refunded: "bg-purple-100 text-purple-900",
  partially_refunded: "bg-fuchsia-100 text-fuchsia-900",
};

export default function AdminOrders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";  // Admin = read-only on order status (per product policy)
  const [orders, setOrders] = useState([]);
  const [refundFor, setRefundFor] = useState(null);
  const [filter, setFilter] = useState("all"); // all | preorder | subscription | special
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");

  const load = () => api.get("/admin/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "preorder") return orders.filter(o => o.type === "preorder");
    if (filter === "subscription") return orders.filter(o => o.type === "subscription");
    if (filter === "special") return orders.filter(o => o.type === "special" || o.type === "special_purchase");
    return orders.filter(o => o.type === filter);
  }, [orders, filter]);

  const update = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Order ${status}`); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const dayEnd = async () => {
    if (!window.confirm("Day-end will mark every open order today as DELIVERED. Refunds for delivered orders must then be handled offline. Continue?")) return;
    try {
      const { data } = await api.post("/admin/day-end", {});
      toast.success(`Day closed — ${data.delivered_count} orders marked delivered`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Orders</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">All orders</h1>
            {isAdmin && <p className="text-xs text-ink-500 mt-1">Read-only view. Status updates happen at counters / POS.</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex bg-cream-200 rounded-full p-1 text-xs font-bold" data-testid="orders-filter">
              {[["all","All"],["preorder","Pre-orders"],["subscription","Subscription"],["special","Specials"]].map(([k,l]) => (
                <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-full ${filter === k ? "bg-ink text-white" : "text-ink-700 hover:bg-cream-300"}`}>{l}</button>
              ))}
            </div>
            {isAdmin && (
              <button data-testid="day-end-btn" onClick={dayEnd}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-white text-xs font-bold hover:bg-brand transition-colors">
                <CalendarCheck size={14}/> Day-end · Mark all delivered
              </button>
            )}
            {isAdmin && (
              <button data-testid="export-orders-btn" onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  if (exportStart) params.set("start", exportStart);
                  if (exportEnd) params.set("end", exportEnd);
                  if (filter !== "all") params.set("type", filter);
                  const { data } = await api.get(`/orders/export?${params}`, { responseType: "text" });
                  const blob = new Blob([data], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = "orders_export.csv"; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("CSV downloaded");
                } catch (e) { toast.error(formatErr(e)); }
              }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700">
                <Download size={14}/> Export CSV
              </button>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 mt-2 mb-3">
            <span className="text-xs text-ink-500">Date range for export:</span>
            <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} className="rounded-lg border border-border px-2 py-1 text-xs" />
            <span className="text-xs text-ink-400">to</span>
            <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} className="rounded-lg border border-border px-2 py-1 text-xs" />
          </div>
        )}
        <div className="bg-white rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-200 text-ink-700">
              <tr>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">When</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Type</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Items</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Total</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Pay</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => {
                const isPreorder = (o.scheduled_date && o.scheduled_date > new Date().toISOString().slice(0,10)) || o.type === "special";
                return (
                <tr key={o.id} className="border-t border-border" data-testid={`order-row-${o.id}`}>
                  <td className="p-3 whitespace-nowrap text-ink-600">
                    {new Date(o.created_at).toLocaleString()}
                    {isPreorder && <div className="text-[10px] text-amber-700 font-bold mt-0.5">PRE-ORDER for {o.scheduled_date}</div>}
                  </td>
                  <td className="p-3 uppercase text-xs font-bold">{o.type}{o.special_title && <div className="text-[10px] text-amber-700 normal-case font-bold">{o.special_title}</div>}</td>
                  <td className="p-3 max-w-xs truncate">{o.items.map(i => `${i.qty}× ${i.name}`).join(", ")}</td>
                  <td className="p-3 text-right font-bold">
                    {inr(o.total)}
                    {(o.refunded_amount || 0) > 0 && (
                      <div className="text-[10px] text-purple-700 font-mono">−{inrMoney(o.refunded_amount)} refunded</div>
                    )}
                  </td>
                  <td className="p-3 uppercase text-xs">{o.payment_method}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${STATUS_COLOR[o.status] || "bg-cream-300"}`}>{o.status?.replace("_", " ")}</span></td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1 flex-wrap">
                      {!isAdmin && o.status === "pending" && <button data-testid={`prep-${o.id}`} onClick={() => update(o.id, "preparing")} className="px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-900 hover:bg-blue-200">Prepare</button>}
                      {!isAdmin && o.status === "preparing" && <button data-testid={`ready-${o.id}`} onClick={() => update(o.id, "ready")} className="px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-900 hover:bg-emerald-200">Ready</button>}
                      {!isAdmin && o.status === "ready" && <button data-testid={`done-${o.id}`} onClick={() => update(o.id, "completed")} className="px-2.5 py-1 rounded-md text-xs font-semibold bg-ink text-white">Complete</button>}
                      {o.status !== "refunded" && o.status !== "cancelled" && o.status !== "delivered" && (o.total > 0) && (
                        <button data-testid={`refund-${o.id}`} onClick={() => setRefundFor(o)}
                          className="px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-900 hover:bg-purple-200 inline-flex items-center gap-1">
                          <RefreshCw size={11}/> Refund
                        </button>
                      )}
                      {o.status === "delivered" && (
                        <span data-testid={`delivered-locked-${o.id}`} title="Refunds for delivered orders are handled offline" className="px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                          <CalendarCheck size={11}/> Delivered
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-ink-500">No orders match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {refundFor && <RefundModal order={refundFor} onClose={() => setRefundFor(null)} onDone={() => { setRefundFor(null); load(); }} />}
    </AppShell>
  );
}

// --------------------------------------------------
function RefundModal({ order, onClose, onDone }) {
  const [type, setType] = useState("full");
  const [picks, setPicks] = useState({});  // menu_item_id -> qty
  const [adminFee, setAdminFee] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const partialSubtotal = useMemo(() => {
    let s = 0;
    for (const it of order.items || []) {
      const q = picks[it.menu_item_id] || 0;
      if (q > 0) {
        const unit = (it.subtotal || 0) / Math.max(it.qty || 1, 1);
        s += unit * q;
      }
    }
    return Math.round(s * 100) / 100;
  }, [picks, order]);

  const refundedAlready = order.refunded_amount || 0;
  const remaining = Math.max(0, (order.total || 0) - refundedAlready);
  const refundSubtotal = type === "full" ? remaining : Math.min(partialSubtotal, remaining);
  const willCredit = Math.max(0, Math.round((refundSubtotal - Number(adminFee || 0)) * 100) / 100);

  const submit = async () => {
    if (refundSubtotal <= 0) return toast.error("Pick something to refund");
    if (Number(adminFee) > refundSubtotal) return toast.error("Admin fee can't exceed refund subtotal");
    setBusy(true);
    try {
      const body = {
        refund_type: type,
        admin_fee: Number(adminFee || 0),
        reason: reason || "",
      };
      if (type === "partial") {
        body.items = Object.entries(picks)
          .filter(([_, q]) => Number(q) > 0)
          .map(([menu_item_id, q]) => ({ menu_item_id, qty: Number(q) }));
        if (body.items.length === 0) { toast.error("Pick at least one item"); setBusy(false); return; }
      }
      const { data } = await api.post(`/orders/${order.id}/refund`, body);
      toast.success(`Refunded ${inr(data.refund_credited)} to ${data.credited_to}`);
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 overflow-y-auto py-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-7 w-full max-w-lg my-auto" data-testid="refund-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-700">Refund order</div>
            <div className="font-heading text-2xl font-bold text-ink mt-1">#{order.id.slice(0, 8).toUpperCase()}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-cream-200 inline-flex items-center justify-center"><X size={16}/></button>
        </div>

        <div className="text-xs text-ink-500 mb-3">
          Total <b className="text-ink">{inr(order.total)}</b> · paid via <b className="text-ink uppercase">{order.payment_method}</b>
          {refundedAlready > 0 && <> · already refunded <b className="text-purple-700">{inrMoney(refundedAlready)}</b> · remaining <b className="text-ink">{inrMoney(remaining)}</b></>}
        </div>

        <div className="inline-flex bg-cream-200 rounded-full p-1 mb-3">
          <button data-testid="refund-type-full" onClick={() => setType("full")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${type === "full" ? "bg-ink text-white" : "text-ink-700"}`}>Full</button>
          <button data-testid="refund-type-partial" onClick={() => setType("partial")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${type === "partial" ? "bg-ink text-white" : "text-ink-700"}`}>Partial</button>
        </div>

        {type === "partial" && (
          <div className="bg-cream-100 rounded-2xl p-3 space-y-2 mb-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Pick items + qty to refund</div>
            {(order.items || []).map((it, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`refund-line-${idx}`}>
                <div className="flex-1">
                  <div className="font-semibold">{it.name}</div>
                  <div className="text-xs text-ink-500">{it.qty}× originally · unit {inr((it.subtotal || 0) / Math.max(it.qty || 1, 1))}</div>
                </div>
                <input type="number" min={0} max={it.qty} value={picks[it.menu_item_id] || 0}
                  onChange={e => setPicks(p => ({ ...p, [it.menu_item_id]: Number(e.target.value) }))}
                  className="w-20 px-2 py-1 rounded border border-border text-sm" />
                <span className="w-16 text-right text-xs text-ink-700">{inrMoney((picks[it.menu_item_id] || 0) * (it.subtotal || 0) / Math.max(it.qty || 1, 1))}</span>
              </div>
            ))}
            <div className="text-xs text-ink-500 text-right">Partial subtotal: <b className="text-ink">{inrMoney(partialSubtotal)}</b></div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Admin fee (₹) — withheld</label>
            <input data-testid="refund-fee" type="number" min={0} value={adminFee} onChange={e => setAdminFee(e.target.value)}
              placeholder="e.g., 20"
              className="mt-1 w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            <div className="text-[10px] text-ink-500 mt-0.5">Withhold an admin/processing fee from the refund.</div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Reason</label>
            <input data-testid="refund-reason" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Customer request"
              className="mt-1 w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
          </div>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mt-4 flex items-start gap-3">
          <BadgeIndianRupee size={18} className="text-emerald-700 flex-shrink-0"/>
          <div className="text-sm">
            <div className="font-bold">{inrMoney(willCredit)} will be credited to source ({order.payment_method?.toUpperCase()})</div>
            <div className="text-xs text-ink-600 mt-0.5">Refund subtotal {inrMoney(refundSubtotal)} − admin fee {inrMoney(Number(adminFee || 0))}</div>
          </div>
        </div>

        {order.payment_method === "cashfree" && (
          <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2 flex items-start gap-2">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5"/>
            <span>Cashfree refunds: in mock mode this is logged immediately. With real keys, the refund is sent to Cashfree and may take 5–7 business days.</span>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
          <button data-testid="refund-submit" disabled={busy || willCredit <= 0} onClick={submit}
            className="px-5 py-2.5 rounded-full bg-purple-600 text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refund {inrMoney(willCredit)}
          </button>
        </div>
      </div>
    </div>
  );
}
